/**
 * Product analytics — the only module that talks to an analytics SDK.
 *
 * Fans out to:
 *  - PostHog (product analytics, tracking plan v1). Only production builds
 *    served from a real host send; dev, test and local builds are a no-op.
 *  - gtag (GTM / Google Ads, already in public/index.html). Only the GA
 *    ecommerce events that existed before are forwarded, under their GA names,
 *    plus page_view on every route change.
 *
 * Rules (tracking plan): no PII (email, phone, names, coupon or voucher code
 * values), no supplier / cost / stock info, snake_case past-tense events,
 * money as major units + ISO currency. Every call swallows its own errors —
 * tracking must never break a purchase.
 */
import { useEffect } from 'react';
import posthog from 'posthog-js';

const POSTHOG_KEY = process.env.REACT_APP_POSTHOG_KEY;
const POSTHOG_HOST = process.env.REACT_APP_POSTHOG_HOST || 'https://us.i.posthog.com';
const APP_ENV = process.env.REACT_APP_ENV || 'production';
const APP_VERSION = process.env.REACT_APP_VERSION || null;

const SURFACE = 'partner_portal';
const CHANNEL = 'partner_portal';

const FIRST_TOUCH_KEY = 'fleek_first_touch';
// { slug, key }: the backend's partner_key for the portal slug it was served
// under, so a returning visit reports the right partner before config loads.
const PARTNER_KEY_KEY = 'fleek_partner_key';
const PURCHASES_KEY = 'fleek_tracked_purchases';
const REVEALED_KEY = 'fleek_revealed_codes';
const RESULTS_KEY = 'fleek_payment_results';
const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

const safe = (fn) => {
  try {
    return fn();
  } catch {
    return undefined;
  }
};

const isLocalHost = () =>
  safe(() => {
    const h = window.location.hostname;
    return h === 'localhost' || h === '127.0.0.1' || h === '[::1]' || h.endsWith('.localhost');
  }) ?? true;

const posthogEnabled = () =>
  Boolean(POSTHOG_KEY) &&
  process.env.NODE_ENV === 'production' &&
  typeof window !== 'undefined' &&
  !isLocalHost();

let started = false;

const gtag = (...args) =>
  safe(() => {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag(...args);
    }
  });

const readJson = (storage, key, fallback) =>
  safe(() => {
    const raw = storage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  }) ?? fallback;

const writeJson = (storage, key, value) =>
  safe(() => storage.setItem(key, JSON.stringify(value)));

/** Remembers `id` under `key`; returns false if it was already there. */
const once = (storage, key, id, cap = 100) => {
  const seen = readJson(storage, key, []);
  const list = Array.isArray(seen) ? seen : [];
  if (list.includes(String(id))) return false;
  writeJson(storage, key, [...list, String(id)].slice(-cap));
  return true;
};

const toNumber = (value) => {
  if (value == null || value === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const upperCurrency = (currency) => (currency ? String(currency).toUpperCase() : 'INR');

/** Origin + path only: a referrer's query string can carry anything. */
const cleanReferrer = (referrer) =>
  safe(() => {
    if (!referrer) return null;
    const url = new URL(referrer);
    return `${url.origin}${url.pathname}`;
  }) ?? null;

/** First-touch attribution, captured on the first landing and kept for good. */
const captureFirstTouch = () =>
  safe(() => {
    const existing = readJson(localStorage, FIRST_TOUCH_KEY, null);
    if (existing) return existing;

    const params = new URLSearchParams(window.location.search);
    const touch = {};
    UTM_KEYS.forEach((k) => {
      touch[k] = params.get(k) || null;
    });
    touch.referrer = cleanReferrer(document.referrer);
    touch.landing_path = window.location.pathname;
    writeJson(localStorage, FIRST_TOUCH_KEY, touch);
    return touch;
  }) || {};

/* ------------------------------------------------------------------ */
/* Core wrapper                                                        */
/* ------------------------------------------------------------------ */

const currentSlug = () => safe(() => localStorage.getItem('partnerSlug')) || null;

/**
 * The partner key to report for `slug`. The backend's partner_key (the same
 * key the dashboard and the server events use) once a config for this slug
 * has been seen; until then, or against an older backend, the slug itself.
 */
const partnerKeyFor = (slug) => {
  const stored = readJson(localStorage, PARTNER_KEY_KEY, null);
  if (stored && stored.key && stored.slug === slug) return stored.key;
  return slug;
};

const registerPartner = (key, slug) => {
  posthog.register({ partner: key, portal_slug: slug });
  if (key) posthog.group('partner', key);
};

/** Call once at startup, before the first page(). */
export const initAnalytics = () =>
  safe(() => {
    if (started) return;
    started = true;

    const firstTouch = captureFirstTouch();

    // GA / Ads get the first-touch UTMs as user properties.
    const gaTouch = {};
    UTM_KEYS.forEach((k) => {
      if (firstTouch[k]) gaTouch[`first_${k}`] = firstTouch[k];
    });
    if (Object.keys(gaTouch).length) gtag('set', 'user_properties', gaTouch);

    if (!posthogEnabled()) return;

    const slug = currentSlug();

    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: false,
      capture_pageview: false,
      capture_pageleave: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      rageclick: false,
      disable_session_recording: true,
      disable_surveys: true,
      persistence: 'localStorage+cookie',
    });

    posthog.register({
      surface: SURFACE,
      channel: CHANNEL,
      app_version: APP_VERSION,
      env: APP_ENV,
      ...firstTouch,
    });

    registerPartner(partnerKeyFor(slug), slug);
  });

/** Send a product event to PostHog. */
export const track = (event, props = {}) =>
  safe(() => {
    if (!started || !posthogEnabled()) return;
    posthog.capture(event, props);
  });

/** Send a GA / Ads event through gtag. */
const gaEvent = (event, params = {}) => gtag('event', event, params);

/**
 * One pageview per route change: PostHog $pageview always, gtag page_view
 * except for the landing page (gtag('config') in index.html already sent it).
 */
export const page = ({ path, isLanding = false } = {}) =>
  safe(() => {
    track('$pageview', { path });
    if (!isLanding) {
      gaEvent('page_view', {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  });

/**
 * The portal slug is known (exchanged for a token). It is kept as
 * `portal_slug`; `partner` is the backend's partner_key when one is known for
 * this slug, otherwise the slug until the config arrives.
 */
export const setPartner = (slug) =>
  safe(() => {
    if (!slug || !started || !posthogEnabled()) return;
    registerPartner(partnerKeyFor(slug), slug);
  });

/**
 * The portal config arrived. Its partner_key is the partner's key on every
 * surface (dashboard, storefront, server events). Missing on an older
 * backend, in which case the slug stays in place.
 */
export const setPartnerKey = (partnerKey) =>
  safe(() => {
    if (!partnerKey) return;
    const slug = currentSlug();
    writeJson(localStorage, PARTNER_KEY_KEY, { slug, key: partnerKey });
    if (!started || !posthogEnabled()) return;
    registerPartner(partnerKey, slug);
  });

/**
 * Identify a user by backend id. The portal has no login, so nothing calls
 * this today. Never pass an email or phone.
 */
export const identify = (backendUserId) =>
  safe(() => {
    if (backendUserId == null || !started || !posthogEnabled()) return;
    posthog.identify(`u_${backendUserId}`);
  });

/**
 * Identify an anonymous portal buyer by the backend's pseudonymous buyer_id
 * (returned by create-portal-order, never the email). The backend keys this
 * order's payment and fulfilment events on the same pb_ id, so the browsing
 * before checkout joins to the payment.
 */
export const identifyBuyer = (buyerId) =>
  safe(() => {
    if (!buyerId || !started || !posthogEnabled()) return;
    posthog.identify(`pb_${buyerId}`);
  });

export const reset = () =>
  safe(() => {
    if (!started || !posthogEnabled()) return;
    posthog.reset();
  });

/* ------------------------------------------------------------------ */
/* Funnel events                                                       */
/* ------------------------------------------------------------------ */

const itemProps = (item) => {
  const isBundle = Boolean(item?.is_bundle);
  return {
    plan_id: (isBundle ? item?.id : item?.plan_id) ?? null,
    plan_name: (isBundle ? item?.product_name : item?.plan_name) || null,
    brand: isBundle ? null : item?.vendor_name || null,
    price: toNumber(isBundle ? item?.bundle_cost : item?.plan_cost),
    currency: upperCurrency(item?.currency),
    product_type: isBundle ? 'bundle' : 'plan',
  };
};

const gaItems = (item) => {
  const p = itemProps(item);
  return [
    {
      item_id: p.plan_id,
      item_name: p.product_type === 'bundle' ? p.plan_name : p.brand,
      item_category: p.product_type,
      price: p.price,
    },
  ];
};

export const trackCatalogueViewed = ({ category = 'all', planCount, bundleCount } = {}) =>
  track('catalogue_viewed', {
    category,
    plan_count: planCount,
    bundle_count: bundleCount,
  });

/** product_viewed (PostHog) + view_item (GA). */
export const trackProductViewed = (item) =>
  safe(() => {
    const p = itemProps(item);
    track('product_viewed', p);
    gaEvent('view_item', { currency: p.currency, value: p.price, items: gaItems(item) });
  });

/** coupon_applied (PostHog + GA). Never the code itself. */
export const trackCouponApplied = ({ item, finalAmount }) =>
  safe(() => {
    const p = itemProps(item);
    const final = toNumber(finalAmount);
    const discount = p.price != null && final != null ? Math.max(0, p.price - final) : null;
    track('coupon_applied', {
      plan_id: p.plan_id,
      discount_amount: discount,
      currency: p.currency,
    });
    gaEvent('coupon_applied', { discounted_price: final });
  });

export const trackCouponRejected = ({ item, reason }) =>
  track('coupon_rejected', { plan_id: itemProps(item).plan_id, reason });

/** checkout_started (PostHog) + begin_checkout (GA). */
export const trackCheckoutStarted = ({ item, amount, hasCoupon }) =>
  safe(() => {
    const p = itemProps(item);
    const value = toNumber(amount) ?? p.price;
    track('checkout_started', {
      plan_id: p.plan_id,
      product_type: p.product_type,
      amount: value,
      currency: p.currency,
      has_coupon: Boolean(hasCoupon),
    });
    gaEvent('begin_checkout', { currency: p.currency, value, items: gaItems(item) });
  });

/** GA only, as before. The email itself is never sent anywhere. */
export const trackEmailEntered = () => gaEvent('generate_lead', { email_entered: true });

export const trackPaymentInitiated = ({ orderId, amount, currency, gateway }) =>
  track('payment_initiated', {
    order_id: orderId,
    amount: toNumber(amount),
    currency: upperCurrency(currency),
    gateway,
    method: null, // chosen inside the gateway; the client never learns it
  });

export const trackPaymentAbandoned = ({ orderId, gateway, stage }) =>
  track('payment_abandoned', { order_id: orderId, gateway, stage });

/** Once per order + status per session (callback and order page both report). */
export const trackPaymentResultViewed = ({ orderId, status, reason = null }) =>
  safe(() => {
    if (!once(sessionStorage, RESULTS_KEY, `${orderId}:${status}`)) return;
    track('payment_result_viewed', { order_id: orderId, status, reason });
  });

/**
 * purchase (GA) + Google Ads conversion, once per order id for good —
 * reloading the order page used to report the sale again every time.
 */
export const trackPurchase = (order) =>
  safe(() => {
    if (!order?.order_id) return;
    if (!once(localStorage, PURCHASES_KEY, order.order_id)) return;

    const value = toNumber(order.order_amount);
    const currency = upperCurrency(order.currency);
    gaEvent('purchase', {
      transaction_id: order.order_id,
      value,
      currency,
      items: [
        {
          item_id: order.plan_id || order.partner_plan,
          item_name: order.partner_plan,
          item_category: 'plan',
          price: value,
        },
      ],
    });
    gaEvent('conversion', {
      send_to: 'AW-10792886696/-mBqCI2kmLcbEKjDuZoo',
      transaction_id: order.order_id,
      value,
      currency,
    });
  });

export const trackOrderViewed = ({ orderId, status, hasCode }) =>
  track('order_viewed', { order_id: orderId, status, has_code: Boolean(hasCode) });

/** First time a code is shown for an order (never the code value). */
export const trackCodeRevealed = ({ orderId, planId, deliveryType }) =>
  safe(() => {
    if (!once(localStorage, REVEALED_KEY, orderId)) return;
    track('code_revealed', {
      order_id: orderId,
      plan_id: planId ?? null,
      delivery_type: deliveryType || 'code',
    });
  });

export const trackCodeCopied = ({ orderId, method }) =>
  track('code_copied', { order_id: orderId, method });

export const trackRedeemLinkOpened = ({ orderId, brand }) =>
  track('redeem_link_opened', { order_id: orderId, brand: brand || null });

/**
 * Error text as it may appear in an event. `reason` is often the message the
 * page shows, and server messages can echo what the buyer typed, so anything
 * shaped like an email or UPI handle (name@okhdfc) and any run of 4+ digits
 * (phone numbers, OTPs) is masked. Same rule as the Android app's scrub().
 */
export const scrub = (text) => {
  if (!text) return null;
  return String(text)
    .replace(/[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+/g, '<id>')
    .replace(/\d{4,}/g, '<n>')
    .trim()
    .slice(0, 120);
};

/** A user-visible error, with its text scrubbed. */
export const trackErrorShown = ({ context, reason }) =>
  track('error_shown', {
    context,
    reason: scrub(reason),
  });

/** error_shown whenever `reason` becomes a new non-empty value. */
export const useTrackError = (context, reason) => {
  useEffect(() => {
    if (reason) trackErrorShown({ context, reason });
  }, [context, reason]);
};
